import { apiClient, ApiError } from './apiClient';
import type { Timesheet, TimesheetEntry, Project, CostCentre, Employee, BatchEntryRequest, BatchEntryResponse } from '@/types/timesheet';

/**
 * Pagination response metadata from the API
 */
export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Paginated response for projects
 */
export interface PaginatedProjectsResponse {
  data: Project[];
  pagination: PaginationInfo;
}

/**
 * Parameters for fetching paginated projects
 */
export interface GetProjectsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

function parseTimesheetDates(timesheet: any): Timesheet {
  return {
    ...timesheet,
    weekEnding: timesheet.weekEnding ? new Date(timesheet.weekEnding) : timesheet.weekEnding,
    submittedAt: timesheet.submittedAt ? new Date(timesheet.submittedAt) : timesheet.submittedAt,
    approvedAt: timesheet.approvedAt ? new Date(timesheet.approvedAt) : timesheet.approvedAt,
    rejectedAt: timesheet.rejectedAt ? new Date(timesheet.rejectedAt) : timesheet.rejectedAt,
    recalledAt: timesheet.recalledAt ? new Date(timesheet.recalledAt) : timesheet.recalledAt,
    // Workflow fields (workflowInstanceId, currentStageName, currentStageOrder, totalStages) 
    // are strings/numbers and are preserved by the spread operator
  };
}

function parseProjectDates(project: any): Project {
  return {
    ...project,
    lastUsed: project.lastUsed ? new Date(project.lastUsed) : project.lastUsed,
  };
}

function parseCostCentreDates(costCentre: any): CostCentre {
  return {
    ...costCentre,
    lastUsed: costCentre.lastUsed ? new Date(costCentre.lastUsed) : costCentre.lastUsed,
  };
}

export const timesheetApi = {
  async getTimesheetById(id: string): Promise<Timesheet> {
    const timesheet = await apiClient.get<any>(`/timesheets/${id}`);
    return parseTimesheetDates(timesheet);
  },

  async getTimesheetsByEmployeeId(employeeId: number): Promise<Timesheet[]> {
    const timesheets = await apiClient.get<any[]>(`/timesheets/employee/${employeeId}`);
    return timesheets.map(parseTimesheetDates);
  },

  async getTimesheetsByEmployeeIds(employeeIds: number[]): Promise<Timesheet[]> {
    if (employeeIds.length === 0) {
      return [];
    }
    const queryString = employeeIds.map(id => `employeeIds=${encodeURIComponent(id)}`).join('&');
    const timesheets = await apiClient.get<any[]>(`/timesheets/employees?${queryString}`);
    return timesheets.map(parseTimesheetDates);
  },

  async createTimesheet(timesheet: Timesheet): Promise<Timesheet> {
    // Strip out entry IDs before sending - they are auto-generated by the database
    const timesheetToSend = {
      ...timesheet,
      entries: timesheet.entries.map(({ id, ...entry }) => entry),
    };
    const created = await apiClient.post<any, typeof timesheetToSend>('/timesheets', timesheetToSend);
    return parseTimesheetDates(created);
  },

  async updateTimesheet(id: string, timesheet: Timesheet): Promise<void> {
    // Strip out entry IDs before sending - they are auto-generated by the database
    const timesheetToSend = {
      ...timesheet,
      entries: timesheet.entries.map(({ id, ...entry }) => entry),
    };
    return apiClient.put<void, typeof timesheetToSend>(`/timesheets/${id}`, timesheetToSend);
  },

  async deleteTimesheet(id: string): Promise<void> {
    return apiClient.delete<void>(`/timesheets/${id}`);
  },

  async addTimesheetEntry(timesheetId: string, entry: TimesheetEntry): Promise<TimesheetEntry> {
    // Strip out entry ID before sending - it is auto-generated by the database
    const { id, ...entryWithoutId } = entry;
    return apiClient.post<TimesheetEntry, typeof entryWithoutId>(`/timesheets/${timesheetId}/entries`, entryWithoutId);
  },

  /**
   * Batch update entries for a timesheet.
   * More efficient than updating the entire timesheet when only entries change.
   */
  async batchUpdateEntries(timesheetId: string, request: BatchEntryRequest): Promise<BatchEntryResponse> {
    // Strip out entry IDs from toCreate entries - they are auto-generated by the database
    const requestToSend = {
      ...request,
      toCreate: request.toCreate.map(({ id, ...entry }) => entry),
    };
    return apiClient.post<BatchEntryResponse, typeof requestToSend>(
      `/timesheets/${timesheetId}/entries/batch`,
      requestToSend
    );
  },

  /**
   * Fetch projects with pagination support.
   * @param params - Optional pagination and filter parameters
   * @returns Paginated response with projects and pagination metadata
   */
  async getProjects(params?: GetProjectsParams): Promise<PaginatedProjectsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    
    const queryString = queryParams.toString();
    const url = queryString ? `/projects?${queryString}` : '/projects';
    const response = await apiClient.get<{ data: any[]; pagination?: PaginationInfo }>(url);
    
    const projects = (response.data || []).map(parseProjectDates);
    const pagination = response.pagination || {
      page: params?.page || 1,
      pageSize: params?.pageSize || projects.length,
      totalItems: projects.length,
      totalPages: 1,
      hasNextPage: false,
      hasPreviousPage: false,
    };
    
    return { data: projects, pagination };
  },

  /**
   * Fetch all projects without pagination (for backward compatibility).
   * Used by components that need all projects at once.
   */
  async getAllProjects(): Promise<Project[]> {
    const response = await apiClient.get<{ data: any[] }>('/projects');
    const projects = response.data || [];
    return projects.map(parseProjectDates);
  },

  /**
   * Fetch a single project by ID.
   * Used to load project details for existing timesheet entries.
   * @param id - The project ID
   * @returns The project or null if not found
   */
  async getProjectById(id: string): Promise<Project | null> {
    try {
      const response = await apiClient.get<any>(`/projects/${id}`);
      return parseProjectDates(response);
    } catch (error) {
      // 404 Not Found is expected when projects are deleted but still referenced in old timesheets
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }
      console.error(`Failed to fetch project ${id}:`, error);
      return null;
    }
  },

  async getAllCostCentres(): Promise<CostCentre[]> {
    const costCentres = await apiClient.get<any[]>('/costcentres');
    return costCentres.map(parseCostCentreDates);
  },

  async getCostCentresByProjectCode(projectCode: string): Promise<CostCentre[]> {
    const costCentres = await apiClient.get<any[]>(`/costcentres?projectCode=${encodeURIComponent(projectCode)}`);
    return costCentres.map(parseCostCentreDates);
  },

  /**
   * Fetch a single cost centre by ID.
   * Used to load cost centre details for existing timesheet entries.
   * @param id - The cost centre ID
   * @returns The cost centre or null if not found
   */
  async getCostCentreById(id: string): Promise<CostCentre | null> {
    try {
      const response = await apiClient.get<any>(`/costcentres/${id}`);
      return parseCostCentreDates(response);
    } catch (error) {
      // 404 Not Found is expected when cost centres are deleted but still referenced in old timesheets
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }
      console.error(`Failed to fetch cost centre ${id}:`, error);
      return null;
    }
  },

  async getEmployeeById(id: string): Promise<Employee> {
    return apiClient.get<Employee>(`/employees/${id}`);
  },

  async getAllEmployees(): Promise<Employee[]> {
    return apiClient.get<Employee[]>('/employees');
  },
};
